/**
 * Two-button $ / % segmented control for the seller concession field (issue #240).
 *
 * Shared by the List & Lock Search Rate Options page and the Confirmation page's
 * "Update Seller Concession" form. No jQuery — the two host pages differ in how
 * they wire jQuery Validate, and this widget deliberately knows nothing about it.
 *
 * Expected markup (a header row ABOVE the input, never a sibling after it — jQuery
 * Validate's default errorPlacement is insertAfter(element), so a toggle placed
 * after the input gets the error label injected between the two and visibly jumps
 * the first time a bound is violated):
 *
 *   <div class="concession-unit-toggle" role="group" aria-label="Concession unit">
 *     <button type="button" data-unit="dollar"  aria-pressed="true">$</button>
 *     <button type="button" data-unit="percent" aria-pressed="false">%</button>
 *   </div>
 *
 * type="button" is load-bearing. #search-form is a real <form> with no action, so a
 * button defaulting to type="submit" would navigate away on click, Space or Enter.
 */

const PRESSED = 'aria-pressed';
const DISABLED = 'aria-disabled';

function isDisabled(button) {
    return button.getAttribute(DISABLED) === 'true';
}

/**
 * @param {HTMLElement} container the role="group" element
 * @param {object} [options]
 * @param {function(string, string):void} [options.onChange] (newUnit, previousUnit)
 * @returns {object|null} controller, or null when the container is absent
 */
export function createUnitToggle(container, options) {
    if (!container) {
        return null;
    }

    const settings = options || {};
    const buttons = Array.prototype.slice.call(
        container.querySelectorAll('button[data-unit]'));

    if (buttons.length === 0) {
        return null;
    }

    function render(unit) {
        buttons.forEach(function (button) {
            // JS writes ONLY aria-pressed; the stylesheet derives appearance from
            // [aria-pressed="true"]. There is no parallel .is-active class, which
            // makes an ARIA/visual mismatch structurally impossible rather than
            // merely something tests have to catch.
            button.setAttribute(PRESSED, String(button.getAttribute('data-unit') === unit));
        });
    }

    function currentUnit() {
        const pressed = buttons.filter(function (button) {
            return button.getAttribute(PRESSED) === 'true';
        })[0];

        return pressed ? pressed.getAttribute('data-unit') : buttons[0].getAttribute('data-unit');
    }

    function setUnit(unit, notify) {
        const previous = currentUnit();

        if (unit === previous) {
            return false;
        }

        const target = buttons.filter(function (button) {
            return button.getAttribute('data-unit') === unit;
        })[0];

        if (!target || isDisabled(target)) {
            return false;
        }

        render(unit);

        if (notify !== false && typeof settings.onChange === 'function') {
            settings.onChange(unit, previous);
        }

        return true;
    }

    buttons.forEach(function (button) {
        // Suppress the focus shift that a mousedown would otherwise cause. Without
        // this, clicking the toggle blurs the concession input, and the blur-time
        // validation fires a "required" error against a field the user is in the
        // middle of using.
        button.addEventListener('mousedown', function (e) {
            e.preventDefault();
        });

        button.addEventListener('click', function (e) {
            e.preventDefault();

            // aria-disabled, unlike disabled, keeps the control focusable and
            // clickable — so the sub-threshold state still explains itself to a
            // screen reader instead of vanishing from the tab order. The trade is
            // that the no-op has to be enforced here.
            if (isDisabled(button)) {
                return;
            }

            setUnit(button.getAttribute('data-unit'), true);
        });
    });

    return {
        getUnit: currentUnit,

        /** Switches units and fires onChange. No-op if already there or disabled. */
        setUnit: function (unit) {
            return setUnit(unit, true);
        },

        /** Switches units WITHOUT firing onChange — for setting the initial state. */
        initUnit: function (unit) {
            return setUnit(unit, false);
        },

        /**
         * Marks a unit available or not. Used for the sub-threshold sales price
         * case, where dollar entry has no usable conversion base.
         */
        setUnitEnabled: function (unit, enabled, reason) {
            buttons.forEach(function (button) {
                if (button.getAttribute('data-unit') !== unit) {
                    return;
                }

                button.setAttribute(DISABLED, String(!enabled));

                if (enabled || !reason) {
                    button.removeAttribute('title');
                } else {
                    // A title survives here only because aria-disabled leaves
                    // pointer events on; the stylesheet's
                    // `button[disabled] { pointer-events: none }` rule would have
                    // suppressed it.
                    button.setAttribute('title', reason);
                }
            });
        },

        isUnitEnabled: function (unit) {
            const target = buttons.filter(function (button) {
                return button.getAttribute('data-unit') === unit;
            })[0];

            return !!target && !isDisabled(target);
        }
    };
}
